import type React from "react";
import { Box, Spinner, Text } from "@salestrak/ui";

import { useAppData } from "#app/lib/data";
import { checkPermission } from "#app/lib/hooks/use-permission";

interface RequirePermissionProps {
  perm: string;
  children: React.ReactNode;
}

/**
 * Full-page "You do not have access" state. Used by RequirePermission and
 * by object-level guards (e.g. a workflow route whose workflow isn't in
 * the viewer's visible set).
 */
export function AccessDenied() {
  return (
    <Box
      fill="flex"
     
      direction="column"
      align="center"
      justify="center"
    >
      <Text variant="muted">You do not have access</Text>
    </Box>
  );
}

/**
 * Route-level permission guard. Wraps a gated screen and checks the
 * permission on load — the sidebar hides gated nav items, but screens
 * are still reachable by URL or in-app navigation (e.g. profile/team
 * dialogs), so every gated route must also verify access itself.
 *
 * - While the workspace `me` payload is loading OR not yet present →
 *   spinner. Gating on `isLoading` alone isn't enough: on a workspace
 *   switch `currentUser` stays populated with the PREVIOUS workspace's
 *   permissions while the refetch is in flight, so evaluating the gate
 *   then would decide against stale data. Waiting for `!isLoading` with a
 *   present `currentUser` guarantees we check the right workspace.
 * - No permission → full-page "You do not have access"
 * - Otherwise → renders the screen
 */
export function RequirePermission({ perm, children }: RequirePermissionProps) {
  const { currentUser, isLoading } = useAppData();

  if (isLoading || !currentUser) {
    return (
      <Box fill="flex" direction="column" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!checkPermission(currentUser?.permissions, perm)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/**
 * HOC variant for route components:
 * `export default withPermission("manage.users", MembersPage);`
 */
export function withPermission<P extends object>(
  perm: string,
  Component: React.ComponentType<P>,
): (props: P) => React.ReactElement {
  function PermissionGuardedScreen(props: P) {
    return (
      <RequirePermission perm={perm}>
        <Component {...props} />
      </RequirePermission>
    );
  }
  return PermissionGuardedScreen;
}
